import * as React from 'react';
import { IATextField } from '../../../components/textField/TextField';
import { Helper } from '../../../Helper';
import { IMessageTrackingDataUser } from '../../../interfaces/IMessageTrackingDataUser';
import DateTimeService from '../../../services/DateTimeService';
import { Environment } from '../../../services/Environment';
import { LocalizationService } from '../../../services/LocalizationService';
import MessageService from '../../../services/MessageService';
import { IAPersonaSmall } from '../../../components/personaSmall/PersonaSmall';
import { IAIcon } from '../../icon/Icon';
import * as styles from './PushMessageItem.css';
import { IASpinner } from '../../spinner/Spinner';
import { IFontSlots } from '../../../interfaces/IFontSlots';


export enum IPushMessageOverviewTrackingState {
  Any,
  Success,
  Fail,
  Pending
}
export interface IPushMessageItemProps {
  environment: "Development" | "Test" | "Production";
  token: string;
  tenant: string;
  instance: string;
  messageId: number;
  keyId?: string;
  title: string;
  date: Date;
  highlightColor: string;
  fontSlots: IFontSlots;
}

export interface IPushMessageItemState {
  isOpen?: boolean;
  filterChosen?: "confirmed" | "pending" | "errored" | "";
  searchText?: string;
  amountToShow?: number;
  allUsers?: IMessageTrackingDataUser[],
  confirmedUsers?: IMessageTrackingDataUser[];
  erroredUsers?: IMessageTrackingDataUser[];
  pendingUsers?: IMessageTrackingDataUser[];
  totalConfirmed?: number;
  totalPending?: number;
  totalErrored?: number;
  isLoadingConfirmedOverview?: boolean;
  isLoadingPendingOverview?: boolean;
  isLoadingErroredOverview?: boolean;
  isLoadingUsers?: boolean;
  fontSlots?: IFontSlots;
}

export class IAPushMessageItem extends React.Component<IPushMessageItemProps, IPushMessageItemState> {
  private readonly dateTimeService: DateTimeService = new DateTimeService();
  private readonly localizationService: LocalizationService = new LocalizationService();
  private debounceInputTimerId: ReturnType<typeof setTimeout>;


  constructor(props: IPushMessageItemProps) {
    super(props);
    this.state = {
      isOpen: false,
      filterChosen: "",
      searchText: "",
      amountToShow: 10,
      allUsers: [],
      confirmedUsers: [],
      pendingUsers: [],
      erroredUsers: [],
      totalConfirmed: 0,
      totalPending: 0,
      totalErrored: 0,
      isLoadingConfirmedOverview: true,
      isLoadingPendingOverview: true,
      isLoadingErroredOverview: true,
      isLoadingUsers: false
    };
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
  }

  public componentDidMount() {
    // load overview
    MessageService.getMessageTrackingOverview(
      this.props.environment,
      this.props.token,
      this.props.tenant,
      this.props.instance,
      this.props.messageId
    ).then(result => {
      this.setState({
        totalConfirmed: result.successCount,
        isLoadingConfirmedOverview: false,
        totalErrored: result.failCount,
        isLoadingErroredOverview: false,
        isLoadingPendingOverview: false,
        totalPending: result.pendingCount,
        isLoadingUsers: false
      });
    }).catch(() => this.setState({
      totalConfirmed: 0,
      isLoadingConfirmedOverview: false,
      totalErrored: 0,
      isLoadingErroredOverview: false,
      totalPending: 0,
      isLoadingPendingOverview: false,
    }));
  }

  public componentDidUpdate(_, prevState: IPushMessageItemState) {
    if (!prevState.isOpen && this.state.isOpen && this.state.filterChosen === "") {
      this.onUsersFilterChange("");
    }
  }

  private async onUsersFilterChange(filterChosen: "confirmed" | "pending" | "errored" | ""): Promise<void> {
    var dynamicUsersStateProp = `${filterChosen}Users`;
    var newFilter = filterChosen;

    if (this.state.isOpen) {
      // only toggle filters off when item is open - else show the filter that was clicked
      newFilter = this.state.filterChosen === filterChosen ? "" : filterChosen;
      dynamicUsersStateProp = newFilter !== "" ? `${newFilter}Users` : "allUsers";
    }

    const partialState: IPushMessageItemState = {};
    partialState.filterChosen = newFilter;
    const newAmount = this.state[dynamicUsersStateProp].length % 10 == 0 ? this.state[dynamicUsersStateProp].length + 10 : 10
    partialState.amountToShow = newAmount;
    partialState.searchText = "";
    partialState.isOpen = true;
    this.setState(partialState, () => {
      if (!this.state[dynamicUsersStateProp].length) {
        this.setState({ isLoadingUsers: true }, () => this.loadUsers())
      }
    });
  }

  private async onSearch(searchText: string) {
    const currentSearchText = searchText;
    const currentFilterState = this.getChosenFilter();
    const dynamicUsersStateProp = this.state.filterChosen !== "" ? `${this.state.filterChosen}Users` : "allUsers";
    const partialState = {}

    this.setState({ searchText, amountToShow: 10 }, () => {
      clearTimeout(this.debounceInputTimerId);
      this.debounceInputTimerId = setTimeout(async () => {
        if (this.state.searchText == currentSearchText) {
          this.setState({ isLoadingUsers: true })
          await MessageService.getMessageTrackingData(
            this.props.environment,
            this.props.token,
            this.props.tenant,
            this.props.instance,
            this.props.messageId,
            this.state.amountToShow - 10,
            10,
            this.state.searchText,
            currentFilterState
          ).then(data => {
            partialState[dynamicUsersStateProp] = data.entries;
          })
        }
        partialState["isLoadingUsers"] = false;
        this.setState(partialState)
      }, 500);
    })
  }

  private async loadUsers(): Promise<void> {
    const currentFilterState = this.getChosenFilter()
    const dynamicUsersStateProp = this.state.filterChosen !== "" ? `${this.state.filterChosen}Users` : "allUsers";
    const partialState: IPushMessageItemState = {}

    const loadMoreItems = this.showLoadMore();

    if (this.state.isOpen && loadMoreItems) {
      partialState[dynamicUsersStateProp] = this.state[dynamicUsersStateProp]
      await MessageService.getMessageTrackingData(
        this.props.environment,
        this.props.token,
        this.props.tenant,
        this.props.instance,
        this.props.messageId,
        this.state.amountToShow - 10,
        10,
        this.state.searchText,
        currentFilterState
      ).then(data => {
        partialState[dynamicUsersStateProp].push(...data.entries);
      })
    }
    partialState.isLoadingUsers = false;
    this.setState(partialState)
  }

  private getChosenFilter() {
    switch (this.state.filterChosen) {
      case "confirmed":
        return IPushMessageOverviewTrackingState.Success;
      case "errored":
        return IPushMessageOverviewTrackingState.Fail;
      case "pending":
        return IPushMessageOverviewTrackingState.Pending;
      default:
        return IPushMessageOverviewTrackingState.Any;
    }
  }

  private showLoadMore(): boolean {
    const usersCount = this.state.filterChosen !== "" ? this.state[`total${this.capitalizeFirstLetter(this.state.filterChosen)}`] : this.state.totalConfirmed + this.state.totalErrored + this.state.totalPending;
    const dynamicUsersStateProp = this.state.filterChosen !== "" ? `${this.state.filterChosen}Users` : "allUsers";
    return (this.state[dynamicUsersStateProp].length < usersCount && !this.state.searchText)
  }
  private capitalizeFirstLetter(word: string) {
    return word.charAt(0)?.toUpperCase() + word.slice(1);
  }

  private areThereUsersToShow() {
    switch (this.state.filterChosen) {
      case "confirmed":
        return this.state.confirmedUsers.length > 0;
      case "errored":
        return this.state.erroredUsers.length > 0;
      case "pending":
        return this.state.pendingUsers.length > 0;
      default:
        return this.state.allUsers.length > 0;
    }
  }

  public render(): JSX.Element {
    let users = this.state.filterChosen === "confirmed" ? this.state.confirmedUsers : this.state.filterChosen === "errored" ? this.state.erroredUsers : this.state.filterChosen === "pending" ? this.state.pendingUsers : this.state.allUsers;
    const isLoading = this.state.isLoadingUsers;
    let noUsersToShow = !this.areThereUsersToShow() && !isLoading;
    return (
      <div
        key={this.props.keyId}
        className={styles.IA_pushMessageItem}
        style={{ paddingBottom: this.state.isOpen ? 10 : 0, fontFamily: this.props.fontSlots?.body?.fontFamily }}
      >
        <div
          className={styles.IA_pushMessageContainer}
          style={{ cursor: this.state.isOpen ? "auto" : "pointer" }}
          onClick={() => !this.state.isOpen && this.setState({ isOpen: !this.state.isOpen })}
        >
          <div
            className={styles.IA_pushMessageContent}
          >
            <div
              className={styles.IA_pushMessageColumn}
              style={{ cursor: "pointer" }}
              onClick={() => this.state.isOpen && this.setState({ isOpen: !this.state.isOpen })}
            >
              <p><b style={{ fontFamily: this.props.fontSlots?.heading?.fontFamily }}>{this.props.title}</b></p>
              <p>{this.dateTimeService.ConvertToDDMMYYYYHHMM(this.props.date)}</p>
            </div>
            <div
              className={styles.IA_pushMessageRow}
            >
              <button
                className={[styles.IA_pushMessageFilterButton, "IA_OnFocus"].join(" ")}
                style={{ backgroundColor: this.state.filterChosen === "confirmed" && this.state.isOpen ? "#f3f3f3" : "#ffffff" }}
                onClick={() => this.onUsersFilterChange("confirmed")}
              >
                {this.state.isLoadingConfirmedOverview
                  ?
                  <IASpinner
                    color="green"
                    style={{
                      width: 10,
                      height: 10,
                      marginRight: 10
                    }}
                  />
                  :
                  <IAIcon
                    title={"Checkmark"}
                    color="green"
                    size={15}
                    style={{ marginTop: 4, marginRight: 10 }}
                  />
                }
                <p>{this.state.totalConfirmed}</p>
              </button>
              <button
                className={[styles.IA_pushMessageFilterButton, "IA_OnFocus"].join(" ")}
                style={{ backgroundColor: this.state.filterChosen === "pending" && this.state.isOpen ? "#f3f3f3" : "#ffffff" }}
                onClick={() => this.onUsersFilterChange("pending")}

              >
                {this.state.isLoadingPendingOverview
                  ?
                  <IASpinner
                    color="orange"
                    style={{
                      width: 10,
                      height: 10,
                      marginRight: 10
                    }}
                  />
                  :
                  <IAIcon
                    title={"Clock"}
                    color="orange"
                    size={15}
                    style={{ marginTop: 4, marginRight: 10 }}
                  />
                }
                <p>{this.state.totalPending}</p>
              </button>
              <button
                className={[styles.IA_pushMessageFilterButton, "IA_OnFocus"].join(" ")}
                style={{ backgroundColor: this.state.filterChosen === "errored" && this.state.isOpen ? "#f3f3f3" : "#ffffff" }}
                onClick={() => this.onUsersFilterChange("errored")}
              >
                {this.state.isLoadingErroredOverview
                  ?
                  <IASpinner
                    color="red"
                    style={{
                      width: 10,
                      height: 10,
                      marginRight: 10
                    }}
                  />
                  :
                  <IAIcon
                    title={"Close"}
                    color="red"
                    size={15}
                    style={{ marginTop: 4, marginRight: 10 }}
                  />
                }
                <p>{this.state.totalErrored}</p>
              </button>
            </div>
          </div>
          <IAIcon
            url={this.state.isOpen ? "https://intraactivestorage.blob.core.windows.net/cdn/icons/Chevron%20Up/SVG/ic_fluent_chevron_up_48_regular.svg" : "https://intraactivestorage.blob.core.windows.net/cdn/icons/Chevron%20Down/SVG/ic_fluent_chevron_down_48_regular.svg"}
            color="grey"
            size={12}
            style={{ height: 15 }}
            onClick={() => this.setState({ isOpen: !this.state.isOpen })}
          />
        </div>
        {this.state.isOpen &&
          <>
            {!noUsersToShow &&
              <IATextField
                style={{
                  height: 60,
                  margin: "0 20px",
                  float: "none",
                  width: "auto"
                }}
                onChange={async (input: string) => {
                  await this.onSearch(input)
                }}
                text={this.state.searchText}
                placeholder={this.localizationService.strings.ContentCreator_PushPanel_FindPerson}
                borderRadius={5}
              />
            }
            {noUsersToShow &&
              <p style={{ marginLeft: 20, color: "grey" }}>{this.localizationService.strings.ContentCreator_PushPanel_NoUsersToShow}</p>
            }
            {users && users.map((item, index) => {
              const userImage = Helper.getProfileImageUrlFromImageAPI(Environment.getEnvironmentForImageAPI(this.props.environment), this.props.tenant, this.props.token, item.id);
              var trackingState;
              switch (item.trackingState) {
                case "SentToUser":
                  trackingState = this.localizationService.strings.ContentCreator_PushPanel_SentToUser;
                  break;
                case "HasBeenReceived":
                  trackingState = this.localizationService.strings.ContentCreator_PushPanel_HasBeenReceived;
                  break;
                case "Working":
                  trackingState = this.localizationService.strings.ContentCreator_PushPanel_Working;
                  break;
                case "WorkingInstalling":
                  trackingState = this.localizationService.strings.ContentCreator_PushPanel_WorkingInstalling;
                  break;
                case "FailedToSend":
                  trackingState = this.localizationService.strings.ContentCreator_PushPanel_FailedToSend;
                  break;
                case "FailedNotInstalled":
                  trackingState = this.localizationService.strings.ContentCreator_PushPanel_FailedNotInstalled;
                  break;
                case "UserNotWhitelisted":
                  trackingState = this.localizationService.strings.ContentCreator_PushPanel_UserNotWhitelisted;
                  break;
                case "FailedToSendReachedMaximumRetries":
                  trackingState = this.localizationService.strings.ContentCreator_PushPanel_FailedToSendReachedMaximumRetries;
                  break;
                default:
                  trackingState = "";
                  break;
              }
              return (
                <div className={[styles.IA_pushMessageRow, styles.IA_pushMessagePersonContainer].join(" ")}>
                  <div className={[styles.IA_pushMessageRow, styles.IA_pushMessagePersonInfo].join(" ")}>
                    <IAPersonaSmall
                      key={`${this.props.keyId}_${this.state.filterChosen}_${index}`}
                      profileImage={userImage}
                      name={item.displayName || item.id}
                      metadata={(this.state.filterChosen === "pending" || this.state.filterChosen === "") ? trackingState : ' '}
                      backgroundColor={this.props.highlightColor}
                      highlightColor={this.props.highlightColor}
                    />
                  </div>
                  <p className={styles.IA_pushMessagePersonDate}>{this.dateTimeService.ConvertToDDMMYYYYHHMM(item.trackedDate, true)}</p>
                </div>
              );
            })}
            {isLoading &&
              <IASpinner
                color={this.props.highlightColor}
                style={{
                  width: 40,
                  height: 40
                }}
              />
            }
            {this.showLoadMore() ?
              <div
                style={{ display: "flex", justifyContent: "center" }}
              >
                <button
                  className={[styles.IA_pushMessageLoadMore, "IA_OnFocus"].join(" ")}
                  onClick={async () => {
                    await this.setState({ amountToShow: this.state.amountToShow + 10, isLoadingUsers: true });
                    await this.loadUsers();
                  }}
                >
                  {this.localizationService.strings.ContentCreator_PushPanel_LoadMore}
                  <IAIcon
                    url="https://intraactivestorage.blob.core.windows.net/cdn/icons/Chevron%20Down/SVG/ic_fluent_chevron_down_48_regular.svg"
                    color="grey"
                    size={10}
                    style={{ marginLeft: 10 }}
                  />
                </button>
              </div>
              :
              <></>
            }
          </>
        }
      </div>
    );
  }
}
